import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { CallStatus as PhoneCallStatus } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import {
  findCitationTime,
  type KeyMoment,
  SentimentEnum,
  type HighlightedCitation,
  type KeyMomentType,
  type Task,
  type CallRecord,
} from '@frontend/api-call-intel';
import { PhoneCallsTypes } from '@frontend/api-phone-calls';
import { isUnscheduledKeyMomentRelevant } from '@frontend/call-intel-core';
import { getDisplayName } from '@frontend/get-display-name';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { ChipVariants } from '@frontend/design-system';

const normalizeCallStatus = (status?: string) => String(status || '').toLowerCase();

export const isMissedCallStatus = (status?: string) =>
  status === PhoneCallStatus.CALL_STATUS_MISSED || normalizeCallStatus(status) === 'missed';

export const isAbandonedCallStatus = (status?: string) =>
  status === PhoneCallStatus.CALL_STATUS_ABANDONED || normalizeCallStatus(status) === 'abandoned';

export const isMissedOrAbandonedCallStatus = (status?: string) =>
  isMissedCallStatus(status) || isAbandonedCallStatus(status);

export const shouldHideConversationForCallStatus = ({
  status,
  aiOutcome,
}: {
  status?: string;
  aiOutcome?: AIOutcome;
}) => {
  const isMissedCall = isMissedCallStatus(status);
  const isAbandonedCall = isAbandonedCallStatus(status);
  const isMissedCallHandledByAi = isMissedCall && !!aiOutcome && aiOutcome !== AIOutcome.AI_OUTCOME_UNKNOWN;

  return isAbandonedCall || (isMissedCall && !isMissedCallHandledByAi);
};

/**
 * @deprecated - Please use `getFullName` from `@frontend/get-display-name` instead.
 */
export const getFullName = (firstName?: string, lastName?: string, defaultValue = '') => {
  if (firstName || lastName) {
    return `${firstName || ''} ${lastName || ''}`.trim();
  }
  return defaultValue || '';
};

export const getOfficeUser = (callInfo: HydratedCallRecord, showEmptyValue: string) => {
  const { user, status, direction, sipName } = callInfo;
  // if it is an inbound call with status abandoned or missed, then show `showEmptyValue`, otherwise use sipName
  const defaultValue =
    direction === 'inbound' && isMissedOrAbandonedCallStatus(status) ? showEmptyValue : sipName || showEmptyValue;
  const fullName = getFullName(user?.firstName, user?.lastName, defaultValue);
  return fullName;
};

export const getStatusChipColor = (status: PhoneCallsTypes.CallLog['Status']): ChipVariants => {
  switch (status) {
    case 'abandoned':
      return 'warn';
    case 'missed':
      return 'critical';
    case 'answered':
      return 'primary';
    case 'forwarded':
      return 'eggplant';
    default:
      return 'outline';
  }
};

export const getContactInfo = (call: HydratedCallRecord) => {
  const locationId = call?.locationId ?? '';
  const personId = call?.person?.personId ?? '';
  const originalfirstName = call?.person?.firstName ?? '';
  const originalLastName = call?.person?.lastName ?? '';
  const preferredName = call?.person?.preferredName ?? '';
  const {
    firstName,
    lastName,
    fullName: profileName,
    legalName,
  } = getDisplayName({
    firstName: originalfirstName,
    lastName: originalLastName,
    preferredName,
  });

  const number = formatPhoneNumber(call?.direction === 'inbound' ? call?.callerNumber : call?.dialedNumber);
  return { firstName, lastName, locationId, number, personId, profileName, legalName };
};

export const processTasks = (tasks: Task[] | undefined, transcript: string): Task[] => {
  if (!tasks) return [];

  return tasks.map((task) => {
    let parsedMetadata;
    let citation = '';
    if (task.metadata) {
      try {
        parsedMetadata = JSON.parse(task.metadata);
        citation = parsedMetadata.citation || '';
      } catch (error) {
        console.error('Error parsing metadata:', error);
      }
    }
    return {
      ...task,
      citation,
      citationTime: findCitationTime(transcript, citation),
    } as Task;
  });
};

const mapReasonsToKeyMoments = ({
  type,
  reasons,
  transcript,
}: {
  type: KeyMomentType;
  reasons?: {
    description?: string;
    citations?: string[];
  }[];
  transcript: string;
}): KeyMoment[] => {
  return (reasons ?? []).flatMap((reason) => {
    const description = reason.description?.trim();

    if (!description) {
      return [];
    }

    const citations = (reason.citations ?? [])
      .map((citation): HighlightedCitation | undefined => {
        const time = transcript ? findCitationTime(transcript, citation) : '';

        return time
          ? {
              text: citation,
              time,
            }
          : undefined;
      })
      .filter((citation): citation is HighlightedCitation => !!citation);

    return [
      {
        type,
        description,
        citations,
      },
    ];
  });
};

export const processKeyMoments = (call: CallRecord | undefined, transcript: string): KeyMoment[] => {
  if (!call) {
    return [];
  }

  const keyMoments: KeyMoment[] = [];

  if (isUnscheduledKeyMomentRelevant(call.schedulingOutcomeV2)) {
    keyMoments.push(
      ...mapReasonsToKeyMoments({
        type: 'unscheduled',
        reasons: call.schedulingOutcomeDetails?.reasons,
        transcript,
      })
    );
  }

  if (call.callSentiment?.sentiment === SentimentEnum.SENTIMENT_NEGATIVE) {
    keyMoments.push(
      ...mapReasonsToKeyMoments({
        type: 'unhappy',
        reasons: call.callSentimentDetails?.reasons,
        transcript,
      })
    );
  }

  return keyMoments;
};
